import { useCallback, useMemo, useState } from 'react';
import { PolicyCategoryGroup } from './policy-groups';

export interface PolicyCategoryOpenState {
  isOpen: (category: string) => boolean;
  toggle: (category: string) => void;
  /** Force open — deep links and picking a category in the filters. Never folds. */
  open: (category: string) => void;
}

/**
 * Categories holding a restriction start open; the rest start folded. A user's
 * click is recorded as an override layered on that default, so a refetch can
 * neither re-fold what they opened nor re-open what they folded.
 */
export const usePolicyCategoryOpen = (
  groups: PolicyCategoryGroup[],
): PolicyCategoryOpenState => {
  const [overrides, setOverrides] = useState<Record<string, boolean>>({});

  const defaultOpen = useMemo(
    () =>
      Object.fromEntries(groups.map((group) => [group.category, group.restrictionCount > 0])),
    [groups],
  );

  const isOpen = useCallback(
    (category: string) => overrides[category] ?? defaultOpen[category] ?? false,
    [overrides, defaultOpen],
  );

  const toggle = useCallback(
    (category: string) =>
      setOverrides((prev) => ({
        ...prev,
        [category]: !(prev[category] ?? defaultOpen[category] ?? false),
      })),
    [defaultOpen],
  );

  const open = useCallback(
    (category: string) => setOverrides((prev) => ({ ...prev, [category]: true })),
    [],
  );

  return useMemo(() => ({ isOpen, toggle, open }), [isOpen, toggle, open]);
};
